import { html } from '@crossiety/lit-components';
import BaseMenu from '@crossiety/lit-components/components/base_menu/element.js';
import '@crossiety/lit-components/components/dot_menu/element.js';

import translations from './translations.js';
import styles from './style.lit.css';

/**
 * The dot menu for a group
 *
 * @prop groupId - Id of the group
 * @prop groupName - For menu title
 * @prop favoriteOn - Should show 'favorite/unfavorite' action?
 * @prop favoriteValue - Is group favored?
 * @prop favoriteUrl - Url for toggle favorite action
 * @prop subscribeOn - Should show 'subscribe' action?
 * @prop subscribeValue - Is user subscribed to group?
 * @prop subscribeUrl - Url for toggle subscribe action
 * @prop reportOn - Should show report action?
 * @prop reportUrl - Url for report action
 * @prop blockOn - Should show 'block/unblock' action?
 * @prop blockValue - Is the group blocked by user?
 * @prop blockUrl - Url for toggle block action
 * @prop leaveOn - Should show 'leave group' action?
 * @prop leaveUrl - Url for 'leave group' action
 * @prop settingsOn - Should show 'settings' action?
 * @prop settingsUrl - Url for 'settings' action
 * @prop adminOn - Should show 'admin' action?
 * @prop adminUrl - Url for 'admin' action
 * @prop notLoggedInUrl - Which url to redirect the user if not logged in
 *
 *
 * @slot trigger - For trigger
 */

export default class GroupMenu extends BaseMenu {
  static styles = [BaseMenu.styles, styles];

  /** @internal */
  static translations = translations;

  static properties = {
    // favorite
    favoriteOn: { type: Boolean },
    favoriteValue: { type: Boolean },
    favoriteUrl: { type: String },

    // subscribe
    subscribeOn: { type: Boolean },
    subscribeValue: { type: Boolean },
    subscribeUrl: { type: String },

    // block
    blockOn: { type: Boolean },
    blockValue: { type: Boolean },
    blockUrl: { type: String },

    // leave
    leaveOn: { type: Boolean },
    leaveUrl: { type: String },

    // settings
    settingsOn: { type: Boolean },
    settingsUrl: { type: String },

    // admin
    adminOn: { type: Boolean },
    adminUrl: { type: String },

    // report
    reportOn: { type: Boolean },
    reportUrl: { type: String },

    // other
    groupId: { type: Number },
    groupName: { type: String },
    notLoggedInUrl: { type: String },
  };

  /** @internal */
  get instanceId() {
    return this.groupId;
  }

  /** @internal */
  get favoriteItem() {
    return {
      title: this.t(
        `actions.favorite.${this.booleanTranslation(!this.favoriteValue)}`
      ),
      action: this.actionFor('remoteToggleProp', [
        'favorite',
        null,
        this.toggleFavoriteCallback.bind(this),
      ]),
      icon: this.favoriteValue ? 'star:solid' : 'star',
      partName: `favorite ${this.favoriteValue ? 'dot-item-active' : ''}`,
    };
  }

  /** @internal */
  get subscribeItem() {
    return {
      title: this.t(
        `actions.subscribe.${this.booleanTranslation(!this.subscribeValue)}`
      ),
      action: this.publicActionFor('remoteToggleProp', [
        'subscribe',
        null,
        this.toggleSubscribeCallback.bind(this),
      ]),
      icon: this.subscribeValue ? 'bell:solid' : 'bell',
      partName: `subscribe ${this.subscribeValue ? 'dot-item-active' : ''}`,
    };
  }

  /** @internal */
  get reportItem() {
    return {
      title: this.t('actions.inappropriate_report'),
      action: this.publicActionFor('execJSFromResponse', [this.reportUrl]),
      icon: 'hexagon-exclamation',
      partName: 'report',
    };
  }

  /** @internal */
  get blockItem() {
    return {
      title: this.t(
        `actions.block.${this.booleanTranslation(!this.blockValue)}`
      ),
      action: this.actionFor('remoteToggleProp', [
        'block',
        this.blockValue ? null : this.t('snack.block.confirm'),
      ]),
      icon: 'ban',
      partName: 'block',
    };
  }

  /** @internal */
  get settingsItem() {
    return {
      title: this.t(`actions.settings`),
      action: this.actionFor('visit', [this.settingsUrl]),
      icon: 'cog',
      partName: `settings`,
    };
  }

  /** @internal */
  get divider() {
    return {
      isDivider: true,
    };
  }

  /** @internal */
  get leaveItem() {
    return {
      title: this.t(`actions.leave`),
      action: this.actionFor('leaveGroup'),
      icon: 'user-group',
      partName: `leave destructive`,
    };
  }

  /** @internal */
  get adminItem() {
    return {
      title: this.t(`actions.admin`),
      action: this.actionFor('visit', [this.adminUrl]),
      icon: 'eye',
      partName: `admin`,
    };
  }

  /** @internal */
  get items() {
    return [
      this.favoriteOn && this.favoriteItem,
      this.subscribeOn && this.subscribeItem,
      this.reportOn && this.reportItem,
      this.blockOn && this.blockItem,

      // dividerOn = hasOptionsAbove && hasOptionsBelow
      (this.favoriteOn || this.subscribeOn || this.reportOn || this.blockOn) &&
        (this.leaveOn || this.settingsOn || this.adminOn) &&
        this.divider,

      this.leaveOn && this.leaveItem,
      this.settingsOn && this.settingsItem,
      this.adminOn && this.adminItem,
    ].filter(Boolean);
  }

  /** @internal */
  get dotMenuConfig() {
    return {
      buttons: this.items,
      header: this.groupName,
      showCancel: true,
    };
  }

  actionFor(method, args) {
    return {
      menu: 'xs-group-menu',
      id: this.instanceId,
      method: method,
      ...(args && { args: args }),
    };
  }

  publicActionFor(method, args) {
    return this.AppConfig?.user?.id
      ? this.actionFor(method, args)
      : this.actionFor('visit', [this.notLoggedInUrl]);
  }

  async remoteToggleProp(prop, confirmMessage, callback) {
    if (confirmMessage && !this.commonActions.confirmDialog(confirmMessage))
      return;

    const propUrl = `${prop}Url`;
    const propValue = `${prop}Value`;
    const propSuccessSnack = this.t(
      `snack.${prop}.${this.booleanTranslation(!this[propValue])}`
    );

    try {
      this[propValue] = !this[propValue];
      await this.commonActions.post(this[propUrl]);
      this.commonActions.displaySnackBarSuccess(propSuccessSnack);
      callback?.();
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
      this[propValue] = !this[propValue];
    }
  }

  async execJSFromResponse(url) {
    try {
      await this.commonActions.execJSFromResponse(url);
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  visit(url) {
    return this.commonActions.visit(url);
  }

  async leaveGroup() {
    if (!this.commonActions.confirmDialog(this.t('are_you_sure'))) return;
    try {
      await this.commonActions.post(this.leaveUrl);
    } catch {
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    }
  }

  toggleFavoriteCallback() {
    this.dispatchEvent(
      new Event('group-favorite', { bubbles: true, composed: true })
    );
  }

  toggleSubscribeCallback() {
    this.dispatchEvent(
      new Event('group-subscribe', { bubbles: true, composed: true })
    );
  }

  booleanTranslation(value) {
    return value ? 'on' : 'off';
  }

  render() {
    return html`
      <ef-dot-menu .config=${this.dotMenuConfig}>
        <slot name="trigger" slot="trigger">
          <button class="xs-btn btn-secondary">
            <ef-icon name="ellipsis"></ef-icon>
          </button>
        </slot>
      </ef-dot-menu>
    `;
  }
}

GroupMenu.registerTagName('xs-group-menu');
GroupMenu.registerCommonActions('xs-group-menu');

customElements.get('xs-group-menu') ||
  customElements.define('xs-group-menu', GroupMenu);
